<script setup>
import { computed } from "vue";
import { remoteImageStaticUrl } from "common/utils/remote-image";
import UseSvg from "components/jiffy/vue-cart/UseSvg.vue";

const lowQualityImageUrl = computed(() => remoteImageStaticUrl("gen/pod/perfect-low-quality.png"));
const highQualityImageUrl = computed(() =>
  remoteImageStaticUrl("gen/pod/perfect-higher-quality.png")
);
</script>

<template>
  <section class="print-perfect-section">
    <header class="print-perfect-section__header">
      <h3 class="print-perfect-section__title">Print Perfect Guarantee</h3>
      <p class="print-perfect-section__sub-title">
        Our image technology makes your design print-ready.
      </p>
      <p class="print-perfect-section__sub-title">Want more changes? We’ll handle them for you.</p>
    </header>

    <main class="print-perfect-section__comparisons">
      <div class="print-perfect-section__comparison-item">
        <div class="print-perfect-section__comparison-low">
          <img
            class="print-perfect-section__image print-perfect-section__image--low-quality"
            :src="lowQualityImageUrl"
            alt="Low quality"
          />
        </div>
        <div class="print-perfect-section__caption">From this</div>
      </div>
      <div class="print-perfect-section__comparison-item">
        <div class="print-perfect-section__comparison-high">
          <img
            class="print-perfect-section__image print-perfect-section__image--high-quality"
            :src="highQualityImageUrl"
            alt="High quality"
          />
        </div>
        <div class="print-perfect-section__caption print-perfect-section__caption--high">
          <UseSvg icon="star" type="print" class="print-perfect-section__icon" />
          To this
        </div>
      </div>
    </main>
  </section>
</template>

<style scoped lang="scss">
@import "frontend/init/jiffy/mixin";
@import "frontend/init/pod/settings";

.print-perfect-section {
  color: $pod-gray-modern-900;

  @include with-desktop {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 80px;
  }

  &__header {
    padding: 0 24px 24px;
    text-align: center;

    @include with-desktop {
      text-align: left;
    }
  }

  &__title {
    font-size: 24px;
    font-weight: 600;
    margin-bottom: 8px;

    @include with-desktop {
      font-size: 32px;
      margin-bottom: 15px;
    }
  }

  &__sub-title {
    margin: 0 auto;
    font-size: 14px;
    font-weight: 500;
    width: 296px;

    @include with-desktop {
      font-size: 16px;
      margin: 0;
    }

    & + & {
      @include with-desktop {
        margin-top: 30px;
      }
    }
  }

  &__comparisons {
    display: flex;
    justify-content: center;
    margin-top: 12px;

    @include with-desktop {
      margin-top: 0;
    }
  }

  &__comparison-low {
    height: 221px;
    width: 171px;
    box-shadow: 0 7px 75px 0 rgba(0, 0, 0, 0.25);
    border-radius: 5px 0 0 5px;
    background: $white;
    position: relative;

    @include with-desktop {
      width: 247px;
      height: 300px;
      border-radius: 12px 0 0 12px;
    }
  }

  &__comparison-high {
    height: 221px;
    width: 171px;
    background: url(remote-image("gen/pod/perfect-tshirt.png")) lightgray -62.993px 0px / 171.429%
      100% no-repeat;
    border-radius: 0 5px 5px 0;
    box-shadow: 0 7px 75px 0 rgba(0, 0, 0, 0.2);
    position: relative;

    @include with-desktop {
      width: 247px;
      height: 300px;
      border-radius: 0 12px 12px 0;
      background-position: -85.424px 0px;
    }
  }

  &__image {
    width: 120px;
    height: 132px;
    position: absolute;
    bottom: 32px;
    left: 25px;

    @include with-desktop {
      width: 171px;
      height: 188px;
      bottom: 44px;
      left: 38px;
    }
  }

  &__icon {
    width: 24px;
    height: 24px;

    @include with-desktop {
      width: 32px;
      height: 32px;
    }
  }

  &__caption {
    color: $pod-gray-modern-600;
    font-family: $pod-marker-font;
    font-size: 15px;
    font-weight: 400;
    text-align: center;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: 8px;

    @include with-desktop {
      margin-top: 12px;
      font-size: 20px;
      gap: 4px;
    }

    &--high {
      color: $pod-blue-marker;
    }
  }
}
</style>
